import React, { useEffect, useState } from 'react';
import { ArrowUpRight, Globe } from 'lucide-react';
import { buildApiUrl } from '../utils/api';
import './Portfolio.css';

function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadProjects = async () => {
      try {
        setHasError(false);
        const response = await fetch(buildApiUrl('/api/portfolio-projects'));

        if (!response.ok) {
          throw new Error(`Portfolio request failed with ${response.status}`);
        }

        const payload = await response.json();

        if (!isMounted) {
          return;
        }

        setProjects(Array.isArray(payload.projects) ? payload.projects : []);
      } catch (error) {
        console.error('[Portfolio] Unable to load projects', error);

        if (isMounted) {
          setHasError(true);
          setProjects([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProjects();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <main className="portfolio-shell">
      <section className="portfolio-showcase-section">
        <div className="container">
          <div className="portfolio-page-heading">
            <h1>
              Our <span className="portfolio-heading-versa">Versa</span>tile Portfolio<span className="portfolio-heading-period">.</span>
            </h1>
            <p>If you would like to view more of our portfolio please email us or start a chat.</p>
          </div>

          {isLoading && (
            <div className="portfolio-grid" aria-live="polite">
              {[0, 1, 2, 3].map((item) => (
                <article key={item} className="portfolio-card portfolio-card-skeleton">
                  <div className="portfolio-preview-frame skeleton-block" />
                  <div className="portfolio-card-content">
                    <div className="skeleton-line skeleton-line-short" />
                    <div className="skeleton-line skeleton-line-title" />
                    <div className="skeleton-line" />
                    <div className="skeleton-line skeleton-line-medium" />
                  </div>
                </article>
              ))}
            </div>
          )}

          {!isLoading && projects.length > 0 && (
            <div className="portfolio-grid">
              {projects.map((project) => {
                const previewSrc = project.localPreviewImage || project.previewImageUrl;

                return (
                  <article key={project.id} className="portfolio-card">
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noreferrer"
                    className="portfolio-preview-link"
                    aria-label={`Open ${project.name}`}
                  >
                    <div className="portfolio-preview-frame">
                      {previewSrc ? (
                        <img
                          src={previewSrc}
                          alt={`${project.name} preview`}
                          className="portfolio-preview-image"
                          loading="lazy"
                        />
                      ) : (
                        <div className="portfolio-preview-fallback">
                          <span>{project.domain}</span>
                          <strong>{project.previewHeading || project.liveTitle || project.name}</strong>
                        </div>
                      )}
                    </div>
                  </a>

                  <div className="portfolio-card-content">
                    <div className="portfolio-card-meta">
                      <span className="portfolio-card-category">{project.category}</span>
                      <span className="portfolio-card-live">Live</span>
                    </div>

                    <h3>{project.name}</h3>
                    <p>{project.summary}</p>

                    <div className="portfolio-card-footer">
                      <span className="portfolio-domain">
                        <Globe size={15} />
                        {project.domain}
                      </span>
                      <a href={project.url} target="_blank" rel="noreferrer" className="portfolio-visit">
                        Visit site <ArrowUpRight size={16} />
                      </a>
                    </div>
                  </div>
                  </article>
                );
              })}
            </div>
          )}

          {!isLoading && projects.length === 0 && (
            <div className="portfolio-empty">
              <h3>{hasError ? 'Portfolio unavailable for the moment' : 'No live client sites available right now'}</h3>
              <p>
                The page completed its live scan but didn&apos;t find any reachable projects to display.
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default Portfolio;
